'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { LineTabsList, LineTabsTrigger } from './LineTabs'
import { SegmentedControlItem, SegmentedControlList } from './SegmentedControl'
import { SegmentedTabsList, SegmentedTabsTrigger } from './SegmentedTabs'

type TabsVariant = 'line' | 'segmented-control' | 'segmented-tabs'

const TabsVariantContext = React.createContext<TabsVariant>('segmented-tabs')

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: TabsVariant
}

function TabsList({ variant = 'segmented-tabs', ...props }: TabsListProps) {
  const ListComponent = {
    line: LineTabsList,
    'segmented-control': SegmentedControlList,
    'segmented-tabs': SegmentedTabsList
  }[variant]

  return (
    <TabsVariantContext.Provider value={variant}>
      <ListComponent {...props} />
    </TabsVariantContext.Provider>
  )
}

function TabsTrigger(props: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext)

  const TriggerComponent = {
    line: LineTabsTrigger,
    'segmented-control': SegmentedControlItem,
    'segmented-tabs': SegmentedTabsTrigger
  }[variant]

  return <TriggerComponent {...props} />
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger, type TabsVariant }
